import { useNavigate } from "react-router-dom";

/**
 * Minimalist SVG avatar with 4 clickable zones.
 * Renders male or female silhouette depending on `gender`.
 */
export default function Avatar({ gender = "hombre", onZone, className = "" }) {
  const navigate = useNavigate();
  const handle = (zone) => {
    if (onZone) onZone(zone);
    else navigate("/log");
  };

  const isWoman = gender === "mujer";
  const stroke = "#6B7A75";
  const softFill = "#FFFFFF";

  return (
    <svg
      viewBox="0 0 220 420"
      xmlns="http://www.w3.org/2000/svg"
      className={`avatar-svg w-full h-auto ${className}`}
      role="img"
      aria-label={`Avatar ${gender}`}
      data-testid="avatar-svg"
    >
      <defs>
        <radialGradient id="aura" cx="50%" cy="40%" r="60%">
          <stop offset="0%" stopColor="#E5EBE7" stopOpacity="0.5" />
          <stop offset="100%" stopColor="#E5EBE7" stopOpacity="0" />
        </radialGradient>
      </defs>
      <ellipse cx="110" cy="220" rx="115" ry="200" fill="url(#aura)" />

      {/* HEAD zone -> sleep / thoughts */}
      <g
        className="avatar-zone"
        id="zone-head"
        onClick={() => handle("head")}
        data-testid="avatar-zone-head"
      >
        <circle cx="110" cy="60" r="34" className="zone-fill" fill={softFill} />
        <circle
          cx="110"
          cy="60"
          r="34"
          className="zone-stroke"
          fill="none"
          stroke={stroke}
          strokeWidth="1.5"
        />
        {/* subtle features */}
        <circle cx="100" cy="58" r="1.6" fill={stroke} />
        <circle cx="120" cy="58" r="1.6" fill={stroke} />
        <path d="M101 72 Q110 78 119 72" stroke={stroke} strokeWidth="1.4" fill="none" strokeLinecap="round" />
      </g>

      {/* NECK */}
      <path d="M101 92 L101 108 L119 108 L119 92" fill="#FFFFFF" stroke={stroke} strokeWidth="1.2" />

      {/* TORSO zone -> bienestar / aprendizaje */}
      <g
        className="avatar-zone"
        id="zone-torso"
        onClick={() => handle("torso")}
        data-testid="avatar-zone-torso"
      >
        {isWoman ? (
          <path
            d="M70 112 Q75 105 95 105 L125 105 Q145 105 150 112 L155 220 Q140 232 110 232 Q80 232 65 220 Z"
            className="zone-fill"
            fill={softFill}
          />
        ) : (
          <path
            d="M62 112 Q70 104 95 104 L125 104 Q150 104 158 112 L154 226 Q138 236 110 236 Q82 236 66 226 Z"
            className="zone-fill"
            fill={softFill}
          />
        )}
        {isWoman ? (
          <path
            d="M70 112 Q75 105 95 105 L125 105 Q145 105 150 112 L155 220 Q140 232 110 232 Q80 232 65 220 Z"
            className="zone-stroke"
            fill="none"
            stroke={stroke}
            strokeWidth="1.5"
            strokeLinejoin="round"
          />
        ) : (
          <path
            d="M62 112 Q70 104 95 104 L125 104 Q150 104 158 112 L154 226 Q138 236 110 236 Q82 236 66 226 Z"
            className="zone-stroke"
            fill="none"
            stroke={stroke}
            strokeWidth="1.5"
            strokeLinejoin="round"
          />
        )}

        {/* HEART (clickable subzone overlay) */}
      </g>

      {/* HEART zone -> mood (placed inside torso area but separate click target) */}
      <g
        className="avatar-zone"
        id="zone-heart"
        onClick={(e) => {
          e.stopPropagation();
          handle("heart");
        }}
        data-testid="avatar-zone-heart"
      >
        <path
          d="M110 158 C 102 148, 88 148, 88 162 C 88 176, 110 192, 110 192 C 110 192, 132 176, 132 162 C 132 148, 118 148, 110 158 Z"
          className="zone-fill"
          fill="#F0E2DD"
        />
        <path
          d="M110 158 C 102 148, 88 148, 88 162 C 88 176, 110 192, 110 192 C 110 192, 132 176, 132 162 C 132 148, 118 148, 110 158 Z"
          className="zone-stroke"
          fill="none"
          stroke="#E07A5F"
          strokeWidth="1.6"
        />
      </g>

      {/* ARMS (decorative, non-interactive) */}
      <path d="M62 116 Q44 160 50 230" fill="none" stroke={stroke} strokeWidth="1.4" strokeLinecap="round" />
      <path d="M158 116 Q176 160 170 230" fill="none" stroke={stroke} strokeWidth="1.4" strokeLinecap="round" />

      {/* LEGS zone -> physical */}
      <g
        className="avatar-zone"
        id="zone-legs"
        onClick={() => handle("legs")}
        data-testid="avatar-zone-legs"
      >
        <path
          d="M82 238 L90 388 Q90 396 96 396 L106 396 Q112 396 110 388 L108 240"
          className="zone-fill"
          fill={softFill}
        />
        <path
          d="M82 238 L90 388 Q90 396 96 396 L106 396 Q112 396 110 388 L108 240"
          className="zone-stroke"
          fill="none"
          stroke={stroke}
          strokeWidth="1.5"
          strokeLinejoin="round"
        />
        <path
          d="M138 238 L130 388 Q130 396 124 396 L114 396 Q108 396 110 388 L112 240"
          className="zone-fill"
          fill={softFill}
        />
        <path
          d="M138 238 L130 388 Q130 396 124 396 L114 396 Q108 396 110 388 L112 240"
          className="zone-stroke"
          fill="none"
          stroke={stroke}
          strokeWidth="1.5"
          strokeLinejoin="round"
        />
      </g>

      {/* Hair hint (woman) */}
      {isWoman && (
        <path
          d="M76 60 Q76 28 110 26 Q144 28 144 60 Q144 50 134 46 Q124 42 110 42 Q96 42 86 46 Q76 50 76 60 Z"
          fill="#2C3532"
          opacity="0.6"
        />
      )}

      {/* zone hover labels */}
      <g className="pointer-events-none" fontFamily="DM Sans, sans-serif" fontSize="10" fill="#6B7A75">
        <text x="160" y="60" opacity="0.85">Cabeza</text>
        <text x="160" y="170" opacity="0.85">Ánimo</text>
        <text x="160" y="220" opacity="0.85">Bienestar</text>
        <text x="160" y="330" opacity="0.85">Físico</text>
      </g>
    </svg>
  );
}
